import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { DefaultFetchService } from "app/services/fetchService";
import { getRecentsAnalysis } from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";
import { EDeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

export interface IDomainSearchResult {
    name: string;
    image: string;
    blockStatus: number[];
}

interface IDomainSearchOptions {
    term: string;
    size?: number;
    webSource?: EDeviceType;
    excludeDomains?: string[];
}

export interface IRecentDomain {
    name: string;
    image?: string;
}

export interface IDomainSuggestionsResult {
    suggestions: IDomainSearchResult[];
    isLoading: boolean;
}

const DEFAULT_SUGGESTIONS_SIZE = 30;
const SEARCH_DEBOUNCE_MS = 400;
const STALE_TIME_MS = 5 * 60 * 1000;

const fetchDomainSuggestions = async (options: IDomainSearchOptions): Promise<IDomainSearchResult[]> => {
    const { term, size = DEFAULT_SUGGESTIONS_SIZE, webSource = EDeviceType.DESKTOP, excludeDomains = [] } = options;

    if (!term.trim()) {
        return [];
    }

    const fetchService = DefaultFetchService.getInstance();
    let url = `/autocomplete/websites?size=${size}&term=${encodeURIComponent(term)}&webSource=${webSource}&validate=true`;

    if (excludeDomains.length > 0) {
        url += `&exclude=${encodeURIComponent(excludeDomains.join(","))}`;
    }

    const response = await fetchService.get<IDomainSearchResult[]>(url);

    return response ?? [];
};

const fetchRecentDomains = async (): Promise<IRecentDomain[]> => {
    try {
        const recentItems = await getRecentsAnalysis("website", true);

        if (!recentItems || recentItems.length === 0) {
            return [];
        }

        const seen = new Set<string>();
        return recentItems
            .map((item: any) => ({
                name: item.data?.mainItem || item.name,
                image: item.data?.icon || item.image,
            }))
            .filter((item: IRecentDomain) => {
                if (seen.has(item.name)) {
                    return false;
                }
                seen.add(item.name);
                return true;
            });
    } catch (error) {
        console.error("Error fetching recent domains:", error);
        return [];
    }
};

export const RECENT_DOMAINS_KEY = ["recentDomains"];
const DOMAIN_SUGGESTIONS_KEY = ["domainSuggestions"];

export const useRecentDomains = () => {
    return useQuery({
        queryKey: RECENT_DOMAINS_KEY,
        queryFn: fetchRecentDomains,
        refetchInterval: false,
        staleTime: STALE_TIME_MS,
    });
};

export const useDomainSuggestions = (
    options: IDomainSearchOptions,
    debounceMs: number = SEARCH_DEBOUNCE_MS,
): IDomainSuggestionsResult => {
    const [debouncedTerm, setDebouncedTerm] = useState(options.term);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedTerm(options.term);
        }, debounceMs);

        return () => clearTimeout(timer);
    }, [options.term, debounceMs]);

    const debouncedOptions = {
        ...options,
        term: debouncedTerm,
    };

    const { data, isFetching } = useQuery({
        queryKey: [...DOMAIN_SUGGESTIONS_KEY, debouncedOptions],
        queryFn: () => fetchDomainSuggestions(debouncedOptions),
        refetchInterval: false,
        staleTime: STALE_TIME_MS,
        enabled: !!debouncedTerm.trim(),
        placeholderData: (previousData) => previousData,
    });

    return {
        suggestions: data ?? [],
        // `isFetching` alone reports ready during the debounce window, when the results on screen
        // still answer the previous term.
        isLoading: debouncedTerm !== options.term || isFetching,
    };
};
